import { useCallback, useEffect, useRef, useState } from 'react';
import { createAudio, destroyAudio } from '../helpers/create-audio';

interface AudioState {
  activeId: string | null;
  loading: boolean;
  playing: boolean;
  paused: boolean;
  durationMs: number;
  progressPct: number;
  elapsedMs: number;
}

const INITIAL: AudioState = {
  activeId: null,
  loading: false,
  playing: false,
  paused: false,
  durationMs: 0,
  progressPct: 0,
  elapsedMs: 0,
};

/**
 * Standalone audio playback hook for notification audio.
 * Manages a single HTMLAudioElement and exposes state compatible
 * with ConversationListenButton props.
 */
export function useNotificationAudio(opts?: { onEnded?: () => void }) {
  const [state, setState] = useState<AudioState>(INITIAL);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const rafRef = useRef<number>(0);
  const activeIdRef = useRef<string | null>(null);
  const onEndedRef = useRef(opts?.onEnded);

  // Keep the callback ref fresh without re-running effects
  useEffect(() => {
    onEndedRef.current = opts?.onEnded;
  }, [opts?.onEnded]);

  const stopTick = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
  }, []);

  const tick = useCallback(() => {
    const a = audioRef.current;
    if (!a || a.paused) {
      rafRef.current = 0;
      return;
    }
    const dur = Number.isFinite(a.duration) ? a.duration * 1000 : 0;
    const cur = (a.currentTime || 0) * 1000;
    const pct = dur > 0 ? Math.min(cur / dur, 1) : 0;
    setState((prev) => ({
      ...prev,
      elapsedMs: cur,
      durationMs: dur,
      progressPct: pct,
    }));
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const stop = useCallback(() => {
    stopTick();
    const a = audioRef.current;
    if (a) {
      a.pause();
      a.onended = null;
      a.onerror = null;
      a.ondurationchange = null;
      a.removeAttribute('src');
      a.load();
    }
    activeIdRef.current = null;
    setState(INITIAL);
  }, [stopTick]);

  const start = useCallback(
    (id: string, url: string) => {
      if (!audioRef.current) {
        audioRef.current = createAudio();
      }
      const a = audioRef.current;

      // Stop any current playback
      stopTick();
      a.pause();

      activeIdRef.current = id;
      setState({ ...INITIAL, activeId: id, loading: true });

      a.ondurationchange = () => {
        if (!Number.isFinite(a.duration)) return;
        setState((prev) => ({ ...prev, durationMs: a.duration * 1000 }));
      };

      a.onended = () => {
        stopTick();
        activeIdRef.current = null;
        setState((prev) => ({
          ...prev,
          playing: false,
          paused: false,
          progressPct: 1,
          activeId: null,
        }));
        onEndedRef.current?.();
      };

      a.onerror = () => {
        stopTick();
        activeIdRef.current = null;
        setState(INITIAL);
      };

      a.src = url;
      a.play()
        .then(() => {
          if (activeIdRef.current !== id) return;
          const dur = Number.isFinite(a.duration) ? a.duration * 1000 : 0;
          setState((prev) => ({
            ...prev,
            loading: false,
            playing: true,
            durationMs: dur > 0 ? dur : prev.durationMs,
          }));
          rafRef.current = requestAnimationFrame(tick);
        })
        .catch(() => {
          if (activeIdRef.current !== id) return;
          activeIdRef.current = null;
          setState(INITIAL);
        });
    },
    [stopTick, tick],
  );

  const togglePause = useCallback(() => {
    const a = audioRef.current;
    if (!a || !activeIdRef.current) return;

    if (a.paused) {
      a.play()
        .then(() => {
          setState((prev) => ({ ...prev, playing: true, paused: false }));
          rafRef.current = requestAnimationFrame(tick);
        })
        .catch(() => {});
    } else {
      a.pause();
      stopTick();
      setState((prev) => ({ ...prev, playing: false, paused: true }));
    }
  }, [tick, stopTick]);

  const seek = useCallback((pct: number) => {
    const a = audioRef.current;
    if (!a || !Number.isFinite(a.duration)) return;
    const clamped = Math.max(0, Math.min(1, pct));
    a.currentTime = clamped * a.duration;
    const dur = a.duration * 1000;
    setState((prev) => ({
      ...prev,
      progressPct: clamped,
      elapsedMs: clamped * dur,
    }));
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      stopTick();
      const a = audioRef.current;
      if (a) {
        destroyAudio(a);
        audioRef.current = null;
      }
    };
  }, [stopTick]);

  return {
    ...state,
    start,
    togglePause,
    seek,
    stop,
  };
}
